import type { Report, ReportPatchInput, ReportWriteInput } from '@ofmos/shared/report';
import type { ReportClient, ReportRunResult, RunReportRequest } from '../client';
import type { Transport } from './transport';

/**
 * `ReportClient` over `/api/reports` (`docs/decisions/reports.md` T5 to T7).
 *
 * As thin as `HttpViewClient`, and for the same reason: what a legal report IS gets
 * decided once, on the server, by `reportDefinitionSchema` plus the metadata checks in
 * `reports/service.ts`. Re-checking any of it here would be the second validator INV-6
 * exists to prevent - it would drift from the first the day a field type is added, and it
 * would drift SILENTLY, because both would still say yes to the reports that already work.
 *
 * THE RUN SENDS THE ZONE AND SOMETIMES THE RANGE. The zone travels from the browser on
 * every grouped read (D13) because a bucket key is a wall-clock date. The range is sent
 * only when the reader has moved the switch: absent means "the range this report was saved
 * with", which keeps a link to a report meaning the same thing for everybody.
 */
export class HttpReportClient implements ReportClient {
  constructor(private readonly transport: Transport) {}

  async list(): Promise<readonly Report[]> {
    const response = await this.transport.request<{ reports: readonly Report[] }>({
      method: 'GET',
      path: '/api/reports',
    });
    return response.reports;
  }

  async get(reportId: string): Promise<Report> {
    const response = await this.transport.request<{ report: Report }>({
      method: 'GET',
      path: `/api/reports/${reportId}`,
    });
    return response.report;
  }

  async create(input: ReportWriteInput): Promise<Report> {
    const response = await this.transport.request<{ report: Report }>({
      method: 'POST',
      path: '/api/reports',
      body: input,
    });
    return response.report;
  }

  async update(reportId: string, patch: ReportPatchInput): Promise<Report> {
    const response = await this.transport.request<{ report: Report }>({
      method: 'PATCH',
      path: `/api/reports/${reportId}`,
      body: patch,
    });
    return response.report;
  }

  async remove(reportId: string): Promise<void> {
    await this.transport.request<void>({ method: 'DELETE', path: `/api/reports/${reportId}` });
  }

  async run(reportId: string, options: RunReportRequest = {}): Promise<ReportRunResult> {
    const query: Record<string, string> = {};
    if (options.range !== undefined) query['range'] = options.range;
    if (options.timeZone !== undefined) query['timeZone'] = options.timeZone;
    return this.transport.request<ReportRunResult>({
      method: 'GET',
      path: `/api/reports/${reportId}/run`,
      ...(Object.keys(query).length === 0 ? {} : { query }),
    });
  }
}
